<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row column-align="center">
          <grid-col col-size="4">
            <h1 class="header margin-top-xl">
              <chevron
                @click="back()"/>
              {{getTitle}}
            </h1>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <form v-on:submit.prevent v-on:keypress.enter.prevent="onSave">
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field first-operand">
              <label-variable-multiple-selector
                :disabled="isEdition()"
                :label="$t('rule.variable')"
                :variables="getVariablesMinimal"
                :taggedVariables="getTaggedVariables"
                :tree="getTagsTree"
                v-model="operandOne"
                v-on:tagsChanged="tagsChanged"
                :error="operandOneError"
                :errorMessage="$t('commons.mandatoryFieldMessage')"
                class="margin-right-m"/>
              </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col class="flex" col-size="4">
            <div class="variable-input-field operator">
              <label-select
              :disabled="isEdition()"
              :label="$t('rule.operator')"
              :options="operators"
              v-model="operator"
              :error="operatorError"
              :errorMessage="$t('commons.mandatoryFieldMessage')"
              class="margin-right-m">
              </label-select>
            </div>
            <div class="variable-input-variable-or-constant second-operand">
              <div class="variable-input-field variable-input-variable-or-constant-field">
                <label-variable-selector
                  v-if="varOrConst === 'variable'"
                  v-on:tagsChanged="tagsChanged"
                  :disabled="isEdition()"
                  :label="$t('rule.variable')"
                  :variables="getVariablesMinimal"
                  :taggedVariables="getTaggedVariables"
                  :tree="getTagsTree"
                  v-model="operandTwo"
                  :error="operandTwoError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')" />
                <label-input
                  :disabled="isEdition()"
                  v-if="varOrConst === 'constant'"
                  :label="$t('rule.constant')"
                  :type="'number'"
                  :step="'1.0'"
                  v-model="constant"
                  :error="constantError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')">
                </label-input>
              </div>
              <div class="variable-input-field variable-input-variable-or-constant-toggle">
              <toggle-switch
              :disabled="isEdition()"
              v-on:change="onSecondOperandSelectionTypeChanged"
              :first="radioFirst"
              :second="radioSecond"
              :selected="varOrConst"/>
              </div>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                form-size="m"
                :label="$t('rule.name')"
                :type="'text'"
                v-model="name"
                :error="nameError"
                :errorMessage="$t('commons.mandatoryFieldMessage')">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center" v-if="displayEmailField">
          <grid-col col-size="4">
            <transition name="fade">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                  form-size="m"
                  :label="$t('rule.emails')"
                  :type="'text'"
                  v-model="emails"
                  :error="emailsError"
                  :errorMessage="$t('rule.emailFieldErrorMessage')">
                </label-input>
              </div>
            </transition>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field">
              <checkbox
                id="email-alerting-enabled"
                :label="$t('rule.sendAlertsCheckbox')"
                v-on:change="emailAddressFieldToggle"
                :checked="emails!==''">
              </checkbox>
            </div>
            <div class="variable-input-field align-right">
              <checkbox
                id="rule-enabled"
                :label="$t('rule.enabledCheckbox')"
                v-on:change="onEnabledChanged"
                :checked="enabled">
              </checkbox>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field submit-buttons align-right">
              <br>
              <btn-pill
                :btnText="$t('commons.cancel')"
                v-on:click="onCancel"
                btn-size="m"
                btn-type="secondary">
              </btn-pill>
              <btn-pill
                :btnText="$t('commons.save')"
                v-on:click="onSave"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
        </form>
      </div>
    </main-layout>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import LabelSelect from '@/components/shared/forms/LabelSelect';
import Radio from '@/components/shared/forms/Radio';
import ToggleSwitch from '@/components/shared/forms/ToggleSwitch';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Checkbox from '@/components/shared/forms/Checkbox';
import Alert from '@/components/shared/alert/Alert';
import LabelVariableMultipleSelector from '@/components/variable/LabelVariableMultipleSelector';
import Chevron from '@/components/shared/Chevron';
import LabelVariableSelector from '@/components/variable/LabelVariableSelector';
import variableApi from '@/api/variable';
import variableSelectorUtils from '@/layouts/variableSelectorUtils';

export default {
  name: 'RuleGroupal',
  components: {
    LabelVariableSelector,
    LabelVariableMultipleSelector,
    MainLayout,
    GridRow,
    GridCol,
    LabelInput,
    LabelSelect,
    Radio,
    ToggleSwitch,
    BtnPill,
    Checkbox,
    Alert,
    Chevron
  },
  mixins: [
    variableSelectorUtils
  ],
  data () {
    return {
      editRule: {},
      /* Form fields */
      name: '',
      operandOne: undefined,
      operandTwo: undefined,
      operator: {label: ' '},
      constant: undefined,
      enabled: true,
      emails: '',
      /* Form field errors */
      nameError: false,
      operandOneError: false,
      operandTwoError: false,
      operatorError: false,
      constantError: false,
      emailsError: false,
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      varOrConst: 'constant',
      radioFirst: {name: 'constant', label: this.$t('rule.constant')},
      radioSecond: {name: 'variable', label: this.$t('rule.variable')},
      operators: [
        {
          label: '<',
          value: '<'
        },
        {
          label: '<=',
          value: '<='
        },
        {
          label: '>',
          value: '>'
        },
        {
          label: '>=',
          value: '>='
        },
        {
          label: '=',
          value: '='
        },
        {
          label: '<>',
          value: '<>'
        }
      ],
      displayEmailField: false,
      variablesMinimal: [],
      taggedVariables: []
    };
  },
  created () {
    variableApi.getMinimalVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
      if (this.isEdition()) {
        this.operandOne = this.editRule.varOneIds;
        const operandTwo = getVarById(this.getVariablesMinimal, this.editRule.varTwoId);
        this.operandTwo = operandTwo || {label: ' '};
        this.name = this.editRule.name;
        this.enabled = this.editRule.enabled;
        this.varOrConst = operandTwo ? 'variable' : 'constant';
        this.operator = this.operators.find(operator => operator.value === this.editRule.operator);
        this.constant = this.editRule.constant;
        this.emails = this.editRule.emails;
        if (this.emails !== '') {
          this.displayEmailField = true;
        }
      }
    });
    this.retrieveTagsTree({ context: this });
    this.editRule = this.$route.params;
  },
  mounted () {
    // Hack to prevent double scroll
    document.getElementsByTagName('body')[0].style.overflow = 'hidden';
  },
  computed: {
    ...mapGetters([
      'getTagsTree'
    ]),
    getBreadcrumb () {
      const personalArea = this.$t('menu.workspace');
      const ruleList = this.$t('rule.title');
      const rule = saveOrUpdateText(this);
      return [
        {name: personalArea, link: '/workspace'},
        {name: ruleList, link: '/rule/list'},
        {name: rule, link: '/rule', disabled: true}];
    },
    getTitle () {
      return saveOrUpdateText(this);
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      return this.taggedVariables;
    }
  },
  methods: {
    ...mapActions([
      'retrieveTagsTree',
      'createRule',
      'updateRule'
    ]),
    back () {
      return this.isEdition() ? this.$router.push({name: 'RuleList'}) : this.$router.push({name: 'RuleMenu'});
    },
    isEdition () {
      return this.editRule.id !== undefined;
    },
    onSave () {
      clearErrorPrompts(this);
      if (isValidForm(this)) {
        const operandOne = this.operandOne;
        const operandTwo = this.varOrConst === 'variable' ? this.operandTwo.id : undefined;
        const rule = {
          name: this.name,
          enabled: this.enabled,
          operator: this.operator.value,
          varOneIds: operandOne,
          varTwoId: operandTwo,
          constant: this.varOrConst === 'variable' ? undefined : this.constant,
          emails: this.emails
        };

        if (this.isEdition()) {
          rule.id = this.editRule.id;
          updateRule(this, rule);
        } else {
          createRule(this, rule);
        }
      }
    },
    onCancel () {
      this.$router.push({name: 'RuleList'});
    },
    onSecondOperandSelectionTypeChanged (event) {
      this.varOrConst = event.target.value;
    },
    onEnabledChanged (selected) {
      this.enabled = selected;
    },
    emailAddressFieldToggle (selected) {
      this.displayEmailField = selected;
      if (!selected) {
        this.emails = '';
      }
    }
  }
};

const saveOrUpdateText = (context) => {
  return context.isEdition()
    ? context.$t('rule.editGroupalRule')
    : context.$t('rule.addGroupalRule');
};

const isValidForm = (context) => {
  let hasError = false;
  if (!context.operandOne || context.operandOne.length === 0) {
    context.operandOneError = true;
    hasError = true;
  }

  if (!context.operator.value) {
    context.operatorError = true;
    hasError = true;
  }

  if (context.varOrConst === 'variable' && !context.operandTwo) {
    context.operandTwoError = true;
    hasError = true;
  }

  if (context.varOrConst === 'constant' && (!context.constant || context.constant.length < 1)) {
    context.constantError = true;
    hasError = true;
  }

  if (!context.name || context.name.length < 1) {
    context.nameError = true;
    hasError = true;
  }

  if (!validateEmailAddresses(context.emails)) {
    context.emailsError = true;
    hasError = true;
  }
  return !hasError;
};

const validateEmail = (email) => {
  const regExp = /^([\w-.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return regExp.test(String(email).toLowerCase());
};

const validateEmailAddresses = (emails) => {
  if (emails !== '') {
    const emailList = emails.split(',');
    for (let i = 0; i < emailList.length; i++) {
      const email = emailList[i];
      if (!validateEmail(email.trim())) {
        return false;
      }
    }
  }
  return true;
};

const clearErrorPrompts = (context) => {
  context.operandOneError = false;
  context.operandTwoError = false;
  context.constantError = false;
  context.nameError = false;
  context.operatorError = false;
};

const getVarById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].id) {
      return list[i];
    }
  }
};

const createRule = (context, rule) => {
  context.createRule({context: context, rule}).then(() => {
    const msg = context.$t('rule.successCreation', {name: rule.name});
    context.$router.replace({ name: 'RuleList', params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('rule.errorCreation'), 'error');
  });
};

const updateRule = (context, rule) => {
  const msg = context.$t('rule.successUpdate', {name: rule.name});
  context.updateRule({context: context, rule}).then(() => {
    context.$router.replace({ name: 'RuleList', params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('rule.errorUpdate'), 'error');
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .align-right {
    float: right;
    clear:both;
  }

  .variable-input-field {
    display: inline-block;
  }

  .variable-input-field-name {
    width: 100%;
    margin: 0 0;
  }

  .variable-input-field-unit {
    min-width: 0;
    width: 25%;
    float: right;
    margin: 0 0;
  }

  .first-operand {
    float: left;
    width: 35%;
    margin: 0 0;
    margin-bottom: $sp-margin;
  }

  .operator {
    width: 10%;
    margin: 0 0;
  }

  .second-operand {
    width: 54.5%;
    margin: 0 0;
    float: right;
  }

  .variable-input-variable-or-constant-field {
    width: 75%;
  }

  .variable-input-variable-or-constant-toggle {
    margin-top: 2rem;
    float: right;
    display: inline-block;
  }

  .submit-buttons {
    float: right;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s
  }

  .fade-enter,
  .fade-leave-to
    /* .fade-leave-active in <2.1.8 */ {
    opacity: 0
  }

  .flex {
    display: flex;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/rules/RuleGroupal.vue